/**
 * ALTIORA Radar — Test de valeur réelle (Founder Value Test).
 *
 * Route: /app/radar/value-test
 * Data source: GET /api/intel/community/value-test
 * NO fixtures, NO demo data.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, Sparkles, Target,
          Clock, Eye, EyeOff } from "lucide-react";

const NAVY = "#0a2540";
const CHAMPAGNE = "#c9a34e";
const SLATE = "#5a6b7a";

const TIER_STYLES = {
  A: { color: "#c92a2a", bg: "#fee2e2", label: "AGIR MAINTENANT",
        icon: Target },
  B: { color: "#0a7a3d", bg: "#dcfce7", label: "À ÉVALUER",
        icon: Clock },
  C: { color: "#a16207", bg: "#fef3c7", label: "À SURVEILLER",
        icon: Eye },
  D: { color: "#6b7280", bg: "#e5e7eb", label: "BRUIT / NON PERTINENT",
        icon: EyeOff },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function OppRow({ row }) {
  const findAloneStyle = {
    "OUI":       { bg: "#fee2e2", fg: "#991b1b" },
    "PEUT-ÊTRE": { bg: "#fef3c7", fg: "#92400e" },
    "NON":       { bg: "#dcfce7", fg: "#15803d" },
  }[row.would_find_alone] || {};
  const temporalStyle = {
    UPCOMING:      { bg: "#dcfce7", fg: "#15803d" },
    OPEN_NOW:      { bg: "#dbeafe", fg: "#1e40af" },
    ONGOING:       { bg: "#fef3c7", fg: "#92400e" },
    DEADLINE_SOON: { bg: "#fee2e2", fg: "#991b1b" },
    UNKNOWN_DATE:  { bg: "#e5e7eb", fg: "#6b7280" },
    PAST_EVENT:    { bg: "#fecaca", fg: "#7f1d1d" },
    EXPIRED:       { bg: "#fecaca", fg: "#7f1d1d" },
  }[row.temporal_status] || {};
  return (
    <div className="rounded p-3 bg-white text-sm"
          data-testid={`value-row-${row.title?.slice(0,30)}`}
          style={{ border: `1px solid ${NAVY}22` }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="font-bold" style={{ color: NAVY }}>{row.title}</div>
          <div className="text-xs mt-0.5" style={{ color: SLATE }}>
            {row.organization && <span><b>{row.organization}</b> · </span>}
            {row.location && <span>{row.location} · </span>}
            {row.date && <span>{fmtDate(row.date)}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold" style={{ color: NAVY }}>
            {Math.round(row._global_score)}
          </div>
          <div className="text-[10px] font-semibold"
                style={{ color: TIER_STYLES[row._tier]?.color }}>
            {row._recommendation}
          </div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-1 text-[10px]"
            style={{ color: SLATE }}>
        <span>Pertinence <b style={{ color: NAVY }}>{Math.round(row.relevance)}</b></span>
        <span>Business <b style={{ color: NAVY }}>{Math.round(row.business)}</b></span>
        <span>Confiance <b style={{ color: NAVY }}>{Math.round(row.confidence)}%</b></span>
        <span>
          Trouverais seul :{" "}
          <b className="px-1 py-0.5 rounded"
              style={{ background: findAloneStyle.bg,
                        color: findAloneStyle.fg }}>
            {row.would_find_alone}
          </b>
        </span>
      </div>
      {row.temporal_status && (
        <div className="text-[10px] mt-1">
          <span className="px-1.5 py-0.5 rounded font-semibold"
                style={{ background: temporalStyle.bg, color: temporalStyle.fg }}>
            {row.temporal_status === "UNKNOWN_DATE" ? "DATE À VÉRIFIER" : row.temporal_status}
          </span>
          {row.temporal_reason && (
            <span className="ml-1" style={{ color: SLATE }}>· {row.temporal_reason}</span>
          )}
        </div>
      )}
      {row._hidden && (
        <div className="text-[10px] mt-1 flex items-center gap-1"
              style={{ color: "#a16207" }}>
          <Sparkles size={11} /> Signal caché — probablement invisible ailleurs
        </div>
      )}
      {row.category?.length > 0 && (
        <div className="mt-1 flex gap-1 flex-wrap text-[10px]">
          {row.category.filter(Boolean).map(c => (
            <span key={c} className="px-1.5 py-0.5 rounded"
                    style={{ background: "#eef2f6", color: SLATE }}>{c}</span>
          ))}
        </div>
      )}
      {row.why_reco && (
        <div className="mt-1 text-xs" style={{ color: NAVY }}>
          <b>Pourquoi :</b> {row.why_reco}
        </div>
      )}
      <div className="mt-1 text-xs" style={{ color: NAVY }}>
        <b>Action :</b> {row.action}
        {row.deadline && (
          <span style={{ color: SLATE }}> · Échéance : {fmtDate(row.deadline)}</span>
        )}
      </div>
      {row.source_url && (
        <a href={row.source_url} target="_blank" rel="noopener noreferrer"
            className="text-[10px] mt-1 flex items-center gap-1"
            style={{ color: CHAMPAGNE }}>
          <ExternalLink size={10} /> Source originale
        </a>
      )}
    </div>
  );
}

function TierColumn({ tier, rows }) {
  const st = TIER_STYLES[tier];
  const Icon = st.icon;
  return (
    <div className="flex flex-col gap-2"
          data-testid={`tier-${tier}`}>
      <div className="rounded p-2 flex items-center gap-2"
            style={{ background: st.bg, color: st.color }}>
        <Icon size={16} />
        <div className="font-bold text-sm">Tier {tier} · {st.label}</div>
        <div className="ml-auto text-lg font-bold">{rows.length}</div>
      </div>
      {rows.length === 0 && (
        <div className="text-xs italic p-2" style={{ color: SLATE }}>
          Aucune opportunité dans ce tier.
        </div>
      )}
      {rows.map((r, i) => <OppRow key={i} row={r} />)}
    </div>
  );
}

export default function FounderValueTest() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/intel/community/value-test");
      setData(r.data);
    } catch (e) { toast.error("Chargement échoué"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="p-6 text-sm" style={{ color: SLATE }}>Chargement du rapport…</div>;
  }
  const k = data.kpis || {};
  return (
    <div className="p-4 space-y-4" style={{ background: "#faf9f5", minHeight: "100vh" }}
          data-testid="founder-value-test">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: NAVY }}
              data-testid="value-test-title">
            ALTIORA RADAR — Test de valeur réelle
          </h1>
          <div className="text-xs" style={{ color: SLATE }}>
            Généré le {fmtDate(data.generated_at)} · profil : {data.profile_ecosystems.length} écosystèmes actifs
            · <b>Données 100% réelles, aucune fixture</b>
          </div>
        </div>
        <button onClick={load} data-testid="btn-refresh-value"
                  className="px-2.5 py-1 rounded flex items-center gap-1 text-xs"
                  style={{ background: "white", color: NAVY, border: `1px solid ${NAVY}44` }}>
          <RefreshCw size={12} /> Régénérer
        </button>
      </div>

      {/* KPI Strip — success measured by actionability, not volume */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="value-kpi-strip">
        {[
          ["Opportunités futures", k.future_opportunities],
          ["Pertinentes", k.relevant_opportunities],
          ["Actionnables (Tier A)", k.actionable_opportunities],
          ["Cachées ✨", k.hidden_opportunities],
          ["Discovery Lift · trouvées grâce à ALTIORA", k.discovery_lift_would_not_find_alone],
        ].map(([label, val]) => (
          <div key={label} className="rounded p-3 bg-white"
                data-testid={`value-kpi-${label}`.replace(/[^a-z0-9]/gi,'-').toLowerCase()}>
            <div className="text-[10px] uppercase tracking-wide" style={{ color: SLATE }}>{label}</div>
            <div className="text-2xl font-bold" style={{ color: NAVY }}>{val ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* Temporal Integrity strip — full auditability */}
      <div className="rounded p-3 bg-white text-xs"
            data-testid="temporal-integrity"
            style={{ border: `1px solid ${NAVY}22` }}>
        <div className="font-bold mb-1" style={{ color: NAVY }}>
          Intégrité temporelle · réf. {data.reference_timezone}
        </div>
        <div className="flex flex-wrap gap-3">
          <span>Signaux traités : <b>{k.signals_processed}</b></span>
          <span style={{ color: "#991b1b" }}>
            Exclus (PAST/EXPIRED) : <b>{k.excluded_temporal}</b>
          </span>
          {k.temporal_status_counts && Object.entries(k.temporal_status_counts).map(([st, n]) => (
            <span key={st}>{st}: <b>{n}</b></span>
          ))}
        </div>
      </div>

      {/* "Que puis-je RÉELLEMENT faire aujourd'hui ?" */}
      <div className="rounded-xl p-4"
            data-testid="today-actionable"
            style={{ background: (data.today_actionable || []).length ? "#dcfce7" : "#f1f5f9",
                      border: `1px solid ${NAVY}22` }}>
        <h2 className="text-lg font-bold mb-2" style={{ color: NAVY }}>
          Que puis-je RÉELLEMENT faire aujourd'hui ?
        </h2>
        {(data.today_actionable || []).length === 0 ? (
          <div className="text-sm" style={{ color: SLATE }}>
            <b>0 opportunité Tier A aujourd'hui.</b> C'est honnête —
            aucun événement/appel vérifiable ne mérite votre attention immédiate.
            Cette absence est <i>préférable à une fausse urgence</i>.
            La confiance dans le Radar passe avant le volume.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.today_actionable.map((x, i) => (
              <div key={i} className="rounded p-3 bg-white"
                    data-testid={`today-row-${i}`}
                    style={{ border: `1px solid ${NAVY}22` }}>
                <div className="font-bold" style={{ color: NAVY }}>{x.opportunity}</div>
                <div className="text-[10px] uppercase mt-1"
                      style={{ color: "#a16207" }}>
                  Statut temporel : {x.temporal_status}
                  {x.hidden_signal && " · ✨ Signal caché"}
                </div>
                <div className="text-xs mt-2" style={{ color: NAVY }}>
                  <div><b>Pourquoi vous :</b> {x.why_me}</div>
                  <div><b>Pourquoi maintenant :</b> {x.why_now}</div>
                  <div><b>Action :</b> {x.action}</div>
                  {x.deadline && <div><b>Échéance :</b> {fmtDate(x.deadline)}</div>}
                  <div><b>Format :</b> {x.distance_or_format}</div>
                  <div><b>Confiance :</b> {Math.round(x.confidence)}%</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          ["Taux de bruit", `${Math.round((k.noise_ratio || 0) * 100)}%`],
          ["Sources actives", k.sources_active],
          ["Sources vérifiées", k.sources_verified],
          ["Nouvelles sources", k.sources_new_discovered],
          ["Actions déclenchées", k.actions_triggered],
        ].map(([label, val]) => (
          <div key={label} className="rounded p-2 bg-white text-center"
                data-testid={`value-secondary-${label}`.replace(/[^a-z0-9]/gi,'-').toLowerCase()}>
            <div className="text-[10px] uppercase" style={{ color: SLATE }}>{label}</div>
            <div className="text-lg font-bold" style={{ color: NAVY }}>{val ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* Ce qu'ALTIORA a trouvé pour vous — max 5 */}
      <div className="rounded-xl p-4"
            data-testid="found-for-you"
            style={{ background: (data.found_for_you || []).length ? "#fef3c7" : "#f1f5f9",
                      border: `1px solid ${NAVY}22` }}>
        <h2 className="text-lg font-bold mb-2" style={{ color: NAVY }}>
          Ce qu'ALTIORA a trouvé pour vous
        </h2>
        {(data.found_for_you || []).length === 0 ? (
          <div className="text-sm" style={{ color: SLATE }}>
            <b>0 opportunité recommandée.</b> Aucune opportunité ne franchit
            le Quality Gate Tier A aujourd'hui. Aucune recommandation de remplissage.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.found_for_you.map((x, i) => (
              <div key={i} className="rounded p-3 bg-white text-xs"
                    data-testid={`found-row-${i}`}
                    style={{ border: `1px solid ${NAVY}22` }}>
                <div className="font-bold text-sm" style={{ color: NAVY }}>{x.name}</div>
                <div className="text-[10px] mt-1" style={{ color: SLATE }}>
                  {x.type} · {x.organization || "—"}
                </div>
                <div className="mt-2 space-y-1" style={{ color: NAVY }}>
                  <div><b>Pourquoi vous :</b> {x.why_it_matches}</div>
                  <div><b>Valeur potentielle :</b> {x.potential_value}</div>
                  <div><b>Pourquoi maintenant :</b> {x.why_now}</div>
                  <div><b>Action :</b> {x.action}</div>
                  {x.deadline && <div><b>Échéance :</b> {fmtDate(x.deadline)}</div>}
                  <div><b>Format :</b> {x.distance_or_format}</div>
                  <div><b>Source :</b> {x.source}</div>
                  {x.direct_link && (
                    <div><b>Lien :</b> <a href={x.direct_link} target="_blank" rel="noopener noreferrer"
                                              style={{ color: CHAMPAGNE }}>{x.direct_link}</a></div>
                  )}
                  <div><b>Dernière vérification :</b> {fmtDate(x.last_verified_at)}</div>
                  <div><b>Confiance :</b> {Math.round(x.confidence || 0)}%</div>
                  <div><b>Discovery Lift :</b> <span className="font-semibold">{x.discovery_lift}</span></div>
                  <div className="text-[10px] italic" style={{ color: SLATE }}>
                    {x.ranking_justification}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Que dois-je faire aujourd'hui ? — max 3 actions */}
      <div className="rounded-xl p-4"
            data-testid="todo-today"
            style={{ background: (data.todo_today || []).length ? "#dcfce7" : "#f1f5f9",
                      border: `1px solid ${NAVY}22` }}>
        <h2 className="text-lg font-bold mb-2" style={{ color: NAVY }}>
          Que dois-je faire aujourd'hui ?
        </h2>
        {(data.todo_today || []).length === 0 ? (
          <div className="text-sm" style={{ color: SLATE }}>
            <b>0 action.</b> Aucune opportunité ne franchit le Quality Gate.
            Aucune recommandation de remplissage.
          </div>
        ) : (
          <ul className="list-disc ml-5 text-sm" style={{ color: NAVY }}>
            {data.todo_today.map((t, i) => (
              <li key={i} className="mb-1">
                <b>{t.action}</b>
                {t.deadline && <span> · échéance {fmtDate(t.deadline)}</span>}
                <div className="text-[10px]" style={{ color: SLATE }}>
                  {t.opportunity} · {t.why}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tiers A → D */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mt-2">
        <TierColumn tier="A" rows={data.tier_A || []} />
        <TierColumn tier="B" rows={data.tier_B || []} />
        <TierColumn tier="C" rows={data.tier_C || []} />
        <TierColumn tier="D" rows={data.tier_D || []} />
      </div>

      <div className="text-[10px] italic mt-4" style={{ color: SLATE }}>
        Le succès d'ALTIORA Radar n'est PAS le volume de sources, d'endpoints ou d'événements.
        Il est mesuré par : « combien d'opportunités pertinentes et actionnables
        ALTIORA a-t-il découvertes que je n'aurais probablement pas trouvées seul ? »
      </div>
    </div>
  );
}
